/**
 * launchNavigation — platform-aware external turn-by-turn launcher.
 *
 * Fixes the "ERR_BLOCKED_BY_RESPONSE" symptom where the Google Maps web
 * directions URL refuses to render inside an in-app browser / PWA webview
 * (Google sends framing/embedding headers that block it). Instead we open the
 * driver's chosen navigator via a native deep link where possible and fall
 * back to the web URL in a genuine new tab.
 *
 *   • iOS  → Google Maps app deep link, fallback Apple Maps.
 *   • Android → Google navigation intent, fallback web Google Maps.
 *   • Desktop → web Google Maps in a new browser tab.
 *   • Waze → waze:// deep link on mobile, web Waze fallback / desktop.
 *
 * Presentation/launch only — no dispatch, lifecycle, state or payment logic.
 */

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/.test(navigator.userAgent || "");
}

// Attempt a native app scheme; if the app doesn't take over (page still
// visible after a short delay), open the web fallback in a new tab.
function openWithFallback(deepLink, webUrl) {
  let appOpened = false;
  const onHide = () => { if (document.hidden) appOpened = true; };
  document.addEventListener("visibilitychange", onHide);
  const blurHandler = () => { appOpened = true; };
  window.addEventListener("blur", blurHandler);

  // Trigger the native scheme.
  window.location.href = deepLink;

  setTimeout(() => {
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("blur", blurHandler);
    if (!appOpened) {
      window.open(webUrl, "_blank", "noopener,noreferrer");
    }
  }, 1400);
}

export function launchNavigation({ lat, lng, app = "google" } = {}) {
  if (lat == null || lng == null) return;

  const webGoogle = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
  const webWaze = `https://www.waze.com/ul?ll=${lat}%2C${lng}&navigate=yes`;

  if (app === "waze") {
    if (isIOS() || isAndroid()) {
      openWithFallback(`waze://?ll=${lat},${lng}&navigate=yes`, webWaze);
    } else {
      window.open(webWaze, "_blank", "noopener,noreferrer");
    }
    return;
  }

  // Google Maps (default).
  if (isIOS()) {
    openWithFallback(
      `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`,
      `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`,
    );
  } else if (isAndroid()) {
    openWithFallback(`google.navigation:q=${lat},${lng}&mode=d`, webGoogle);
  } else {
    window.open(webGoogle, "_blank", "noopener,noreferrer");
  }
}
